import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { WbsItemSearchConditionVO } from '../../../../domain/value-object/WbsItemSearchConditionVo'
import WbsItemApi, {
  WbsItemDeltaInput,
  WbsItemSearchDetail,
} from '../../../../lib/functions/wbsItem'
import { useWbsItemSearchConditionApiRequestTransformService } from '../../../../services/transform-service/wbsItemSearchConditionApiRequestTransformService'
import store from '../../../../store'
import { doNotRequireSave } from '../../../../store/requiredSaveData'
import { APIResponse, successDummyResponse } from '../../../../lib/commons/api'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import {
  ActualWork,
  TaskActualWorkProxyRegistrationData,
  TaskActualWorkProxyRegistrationRow,
  TaskActualWorkProxyRegistrationRowBody,
} from '../taskActualWorkProxyRegistration'
import { round } from '../../../../lib/functions/workload'
import TaskActualWorkApi, {
  ProxyTaskActualWorkUpdateBatchRequest,
} from '../../../../lib/functions/taskActualWork'
import { createDelta } from '../../../../domain/value-object/ItemDeltaInputVO'
import { hasDiffDateTerm } from '../../../../utils/date'

export interface Props {
  applicationFunctionUuid: string
  projectUuid: string
  searchCondition: WbsItemSearchConditionVO
  selectedMemberUuid?: string
}

type FetchWbsItemResponase = {
  total: number
  hit: number
  data: WbsItemSearchDetail[]
  offset: number
  limit: number
}

export const useTaskActualWorkProxyRegistrationData = ({
  applicationFunctionUuid,
  projectUuid,
  searchCondition,
  selectedMemberUuid,
}: Props) => {
  const [totalDataSize, setTotalDataSize] = useState<number>(0)
  const [hitDataSize, setHitDataSize] = useState<number>(0)
  const [count, setCount] = useState<number>(0)
  const [data, setData] = useState<TaskActualWorkProxyRegistrationRow[]>([])
  const originalData = useRef<Map<string, TaskActualWorkProxyRegistrationRow>>(
    new Map()
  )

  const searchConditionApiRequestTransformer =
    useWbsItemSearchConditionApiRequestTransformService(projectUuid)

  const searchConditionForApi = useMemo(
    () => searchConditionApiRequestTransformer.toApiRequest(searchCondition),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectUuid, searchCondition]
  )

  useEffect(() => {
    async function fetchData() {
      if (selectedMemberUuid === undefined) return
      const count = await WbsItemApi.countSearchWbsItem(searchConditionForApi)
      const num: number = count.json
      setCount(num)
    }
    fetchData()
  }, [searchConditionForApi])

  const fetch = useCallback(async () => {
    if (!selectedMemberUuid) return
    const wbsItemCountResponse = await WbsItemApi.countSearchWbsItem(
      searchConditionForApi
    )
    const count: number = wbsItemCountResponse.json
    if (count === undefined) return
    if (count > 10000) {
      store.dispatch(
        addScreenMessage(applicationFunctionUuid, {
          type: MessageLevel.WARN,
          title: intl.formatMessage(
            {
              id: 'bulksheet.warning.search.limit.parameterized',
            },
            { n: 10000 }
          ),
        })
      )
      return
    }
    const response = await WbsItemApi.searchTaskAndProxyActualWork(
      searchConditionForApi,
      selectedMemberUuid
    )
    let source: TaskActualWorkProxyRegistrationData[] = response.json.data
    const fetchData: FetchWbsItemResponase = response.json
    setTotalDataSize(fetchData.total)
    setHitDataSize(fetchData.hit)
    const rows: TaskActualWorkProxyRegistrationRow[] = source.map(
      (v, index) => {
        const w = {
          uuid: v.uuid,
          lockVersion: v.lockVersion,
          rowNumber: index + 1,
          body: new TaskActualWorkProxyRegistrationRowBody(v),
        } as TaskActualWorkProxyRegistrationRow
        return w
      }
    )
    setData(rows)
  }, [selectedMemberUuid, searchConditionForApi, applicationFunctionUuid])

  const refresh = useCallback(async () => {
    await fetch()
    store.dispatch(doNotRequireSave())
  }, [fetch])

  const updateTaskActualWork = useCallback(
    async (
      edited: TaskActualWorkProxyRegistrationRow[],
      dateTerm: string[],
      selectedMemberUuid: string | undefined
    ): Promise<APIResponse> => {
      if (!selectedMemberUuid) return successDummyResponse
      const actualWorkBatchRequest: ProxyTaskActualWorkUpdateBatchRequest = {
        ownerTaskActualWorks: [],
      }
      edited.forEach((row: TaskActualWorkProxyRegistrationRow) => {
        dateTerm.forEach(date => {
          const actualWork: ActualWork | undefined =
            row.body?.actualWorks!.find(
              (v: ActualWork) => v.actualDate === date
            )
          const getActualDateTime = (date: string) => {
            return {
              startDateTime: Date.parse(`${date} 00:00:00`),
              endDateTime: Date.parse(`${date} 00:00:00`),
            }
          }
          if (actualWork && actualWork.isEdited) {
            actualWorkBatchRequest.ownerTaskActualWorks.push({
              taskUuid: row.uuid,
              hour:
                actualWork.hour && actualWork.hour >= 0.01
                  ? round(actualWork.hour)
                  : 0,
              actualDate: date.replace(/\//g, '-'),
              actualDateTime: getActualDateTime(date),
              ownerUserUuid: selectedMemberUuid,
            })
          }
        })
      })
      return TaskActualWorkApi.updateProxyBatchDelta(actualWorkBatchRequest)
    },
    []
  )

  const generateUpdateWbsItemDeltaRequest = useCallback(
    (row: TaskActualWorkProxyRegistrationRow): Partial<WbsItemDeltaInput> => {
      if (!row.edited) return {}
      const originalBody = originalData.current.get(row.uuid)
      const original = originalBody!.body?.wbsItem
      const wbsItem = row.body?.wbsItem
      if (!wbsItem) return {}

      return {
        uuid: wbsItem.uuid,
        type: wbsItem.type,
        status: createDelta(original?.status, wbsItem.status),
        substatus: createDelta(original?.substatus, wbsItem.substatus),
        displayName: createDelta(original?.displayName, wbsItem.displayName),
        description: createDelta(original?.description, wbsItem.description),
        teamUuid: createDelta(original?.team?.uuid, wbsItem.team?.uuid),
        accountableUuid: createDelta(
          original?.accountable?.uuid,
          wbsItem.accountable?.uuid
        ),
        responsibleUuid: createDelta(
          original?.responsible?.uuid,
          wbsItem.responsible?.uuid
        ),
        assigneeUuid: createDelta(
          original?.assignee?.uuid,
          wbsItem.assignee?.uuid
        ),
        estimatedStoryPoint: createDelta(
          original?.estimatedStoryPoint,
          wbsItem.estimatedStoryPoint
        ),
        estimatedHour: createDelta(
          original?.estimatedHour,
          wbsItem.estimatedHour
        ),
        priority: createDelta(original?.priority, wbsItem.priority),
        difficulty: createDelta(original?.difficulty, wbsItem.difficulty),
        scheduledDate: hasDiffDateTerm(
          original?.scheduledDate,
          wbsItem.scheduledDate
        )
          ? {
              oldValue: original?.scheduledDate,
              newValue: wbsItem.scheduledDate,
            }
          : undefined,
        actualDate: hasDiffDateTerm(original?.actualDate, wbsItem.actualDate)
          ? {
              oldValue: original?.actualDate,
              newValue: wbsItem.actualDate,
            }
          : undefined,
        estimatedAmount: createDelta(
          original?.estimatedAmount,
          wbsItem.estimatedAmount
        ),
        actualAmount: createDelta(original?.actualAmount, wbsItem.actualAmount),
        sprintUuid: createDelta(original?.sprint?.uuid, wbsItem.sprint?.uuid),
      } as WbsItemDeltaInput
    },
    []
  )

  const save = useCallback(
    async (
      dateTerm: string[],
      selectedMemberUuid: string | undefined
    ): Promise<APIResponse> => {
      const edited = Array.from(data.values()).filter(v => v.edited)
      if (edited.length === 0) return successDummyResponse
      return updateTaskActualWork(edited, dateTerm, selectedMemberUuid)
    },
    [data, updateTaskActualWork]
  )

  return {
    totalDataSize,
    hitDataSize,
    count,
    data,
    refresh,
    save,
    generateUpdateWbsItemDeltaRequest,
  }
}
